import React from 'react';
import { RefreshCw } from 'lucide-react';
import Tooltip from '../ui/Tooltip';
import { randomGradient } from './boardHelpers';
import { NEUTRAL_BOARD_COLOR, isNeutralBoardColor, gradientStops, gradientFrom } from './boardColor';
import './BoardEditModal.css';

/** The one-click colours next to the gradient (artboard EditBoard). Each is
 *  stored like any board colour: a CSS background, here a soft gradient from
 *  the swatch's hue, so tiles and the board header draw it unchanged.
 *  "Neutral" (owner, 13.10.2026) is a dark card in dark mode and a light one
 *  in light mode — see boardColor.js. */
export const BOARD_COLOR_PRESETS = [
  { key: 'neutral', name: 'Neutral — dark in dark mode, light in light mode', value: NEUTRAL_BOARD_COLOR },
  { key: 'green', name: 'Green', swatch: '#34d399', value: 'linear-gradient(135deg, #34d399 0%, #059669 100%)' },
  { key: 'blue', name: 'Blue', swatch: '#60a5fa', value: 'linear-gradient(135deg, #60a5fa 0%, #2563eb 100%)' },
  { key: 'purple', name: 'Purple', swatch: '#a78bfa', value: 'linear-gradient(135deg, #a78bfa 0%, #7c3aed 100%)' },
  { key: 'orange', name: 'Orange', swatch: '#fb923c', value: 'linear-gradient(135deg, #fb923c 0%, #ea580c 100%)' },
];

/** One end of the gradient: a round swatch on the bar with the platform
 *  colour picker under it (the Settings accent-colour pattern). */
function ColorStop({ side, value, onPick }) {
  const label = side === 'from' ? 'Start colour' : 'End colour';
  return (
    <Tooltip content={label} side="top">
      <span className={`board-edit-color-stop board-edit-color-stop--${side}`} style={{ background: value }}>
        <input type="color" value={value} aria-label={label} onChange={(e) => onPick(e.target.value)} />
      </span>
    </Tooltip>
  );
}

/**
 * "Color" in the create / edit board modal: the board's gradient as a bar
 * whose two ends are editable (owner, 13.10.2026: "make the color editable"),
 * the presets, and a shuffle button for a new random gradient.
 */
export default function BoardColorField({ color, onChange }) {
  const neutral = isNeutralBoardColor(color);
  const [from, to] = gradientStops(color);
  return (
    <div className="board-edit-field board-edit-field--wide">
      <span className="board-edit-label">Color</span>
      <div className="board-edit-color-row">
        <div className={`board-edit-color-bar${neutral ? ' board-edit-color-bar--neutral' : ''}`} style={{ background: color }}>
          {/* The neutral preset adapts to the theme, so it has no fixed ends to edit. */}
          {!neutral && <ColorStop side="from" value={from} onPick={(c) => onChange(gradientFrom(c, to))} />}
          {!neutral && <ColorStop side="to" value={to} onPick={(c) => onChange(gradientFrom(from, c))} />}
        </div>
        <div className="board-edit-color-presets">
          {BOARD_COLOR_PRESETS.map((p) => {
            const active = p.key === 'neutral' ? neutral : color === p.value;
            return (
              <Tooltip key={p.key} content={p.name} side="top">
                <button
                  type="button"
                  className={`board-edit-color-preset board-edit-color-preset--${p.key}${active ? ' board-edit-color-preset--active' : ''}`}
                  style={p.swatch ? { background: p.swatch } : undefined}
                  aria-label={`Preset ${p.key}`}
                  aria-pressed={active}
                  onClick={() => onChange(p.value)}
                />
              </Tooltip>
            );
          })}
        </div>
        <Tooltip content="New random gradient" side="top">
          <button type="button" className="board-edit-shuffle" aria-label="New random gradient" onClick={() => onChange(randomGradient())}>
            <RefreshCw size={15} strokeWidth={2.2} aria-hidden />
          </button>
        </Tooltip>
      </div>
    </div>
  );
}
